import { getIntersectionRoot } from '@theme/scroll-container';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const isDesignMode = () =>
  document.documentElement.classList.contains('shopify-design-mode') || Boolean(window.Shopify?.designMode);

/** @type {Set<Element>} */
const watched = new Set();

/**
 * @param {Element} element
 */
const skipRevealInDesignMode = (element) => {
  observer.unobserve(element);
  watched.delete(element);
  element.classList.remove('mox-reveal--pending', 'mox-reveal--visible');
};

const forceRevealAllInDesignMode = () => {
  if (!isDesignMode()) return;

  for (const element of document.querySelectorAll('.mox-reveal')) {
    skipRevealInDesignMode(element);
  }
};

/**
 * @param {Element} element
 * @param {boolean} animate
 */
const reveal = (element, animate) => {
  observer.unobserve(element);
  watched.delete(element);
  element.classList.remove('mox-reveal--pending');
  if (animate) element.classList.add('mox-reveal--visible');
};

const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) reveal(entry.target, true);
    }
  },
  {
    root: getIntersectionRoot(),
    rootMargin: '0px 0px -8%',
    threshold: 0,
  }
);

/**
 * A jump in scroll position (the theme restores `scrollTop` on reload) can leave elements above the
 * viewport without ever crossing the observer threshold, so they would stay hidden forever.
 */
const revealSkipped = () => {
  const root = getIntersectionRoot();
  const rootTop = root ? root.getBoundingClientRect().top : 0;

  for (const element of [...watched]) {
    const rect = element.getBoundingClientRect();
    if (rect.height > 0 && rect.bottom < rootTop) reveal(element, false);
  }
};

const scheduleRevealSkipped = () => {
  requestAnimationFrame(() => requestAnimationFrame(revealSkipped));
};

/**
 * @param {ParentNode} root
 */
const initializeReveals = (root) => {
  const elements = [...root.querySelectorAll('.mox-reveal:not([data-mox-reveal-ready])')];
  const viewportHeight = window.innerHeight;
  const inInitialViewport = elements.map((element) => {
    const rect = element.getBoundingClientRect();
    return rect.top < viewportHeight && rect.bottom > 0;
  });

  for (const [index, element] of elements.entries()) {
    element.setAttribute('data-mox-reveal-ready', '');

    if (reducedMotion.matches || isDesignMode() || inInitialViewport[index]) {
      skipRevealInDesignMode(element);
      continue;
    }

    element.classList.add('mox-reveal--pending');
    watched.add(element);
    observer.observe(element);
  }
};

initializeReveals(document);
forceRevealAllInDesignMode();

window.addEventListener('pageshow', scheduleRevealSkipped);
window.addEventListener('popstate', scheduleRevealSkipped);

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  initializeReveals(event.target);
  forceRevealAllInDesignMode();
});

if (isDesignMode()) {
  document.addEventListener('shopify:section:select', forceRevealAllInDesignMode);

  const scrollTarget = getIntersectionRoot() ?? document;
  let revealScrollFrame = 0;

  scrollTarget.addEventListener(
    'scroll',
    () => {
      if (revealScrollFrame) return;
      revealScrollFrame = requestAnimationFrame(() => {
        revealScrollFrame = 0;
        forceRevealAllInDesignMode();
      });
    },
    { passive: true }
  );
}
